import './BizStaffingCalendar.scss';

import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { localDay, moveDays, getISOWeekNumber } from '../../Utils/calendarUtils';
export { localDay, moveDays } from '../../Utils/calendarUtils';
export function BizStaffingCalendar({
	presentation,
	from,
	to,
	month,
	events,
	onOpen,
}: {
	presentation?: 'planning';
	from: string;
	to: string;
	month?: string;
	events: {
		id: number;
		name: string;
		start: string;
		end?: string;
		isCancelled?: boolean;
		label?: string;
		projection?: string;
		badges?: { text: string; warning?: boolean; tone?: 'info' | 'absent' }[];
		warning?: boolean;
		kind?: 'mine' | 'substitute' | 'available' | 'absent';
	}[];
	onOpen: (id: number) => void;
}) {
	const { t, i18n } = useTranslation();
	const locale = i18n.resolvedLanguage || 'sv-SE';
	const cancelledLabel = t('STAFFING_CALENDAR.CANCELLED', { defaultValue: 'Canceled' });
	const time = (value: string) => new Date(value).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
	const days: string[] = [];
	for (let day = from; day < to && days.length < 93; day = moveDays(day, 1)) days.push(day);
	const names = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
	return (
		<div className={`biz-staffing-calendar${presentation === 'planning' ? ' biz-staffing-calendar--planning' : ''}`}>
			<div className='biz-staffing-calendar__header'>{t('STAFFING_CALENDAR.WEEK', { defaultValue: 'Vecka' })}</div>
			{['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((day, index) => (
				<div key={day} className={`biz-staffing-calendar__header biz-weekday-${day}`}>
					{t(`STAFFING_CALENDAR.${day.toUpperCase()}`, {
						defaultValue: ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'][index],
					})}
				</div>
			))}
			{days.map((day, index) => {
				const date = new Date(`${day}T12:00:00`);
				return (
					<Fragment key={day}>
						{index % 7 === 0 && (
							<div
								className='biz-staffing-calendar__week'
								aria-label={`${t('STAFFING_CALENDAR.WEEK', { defaultValue: 'Vecka' })} ${getISOWeekNumber(date)}`}
							>
								{t('STAFFING_CALENDAR.WEEK_NUMBER', {
									defaultValue: 'v {{number}}',
									number: getISOWeekNumber(date),
								})}
							</div>
						)}
						<section
							className={`biz-staffing-calendar__day biz-weekday-${names[date.getDay()]}${month && !day.startsWith(month) ? ' is-outside' : ''}`}
						>
							<h3 className={`biz-weekday-${names[date.getDay()]}`}>
								{presentation === 'planning' ? (
									<>
										<small>{date.toLocaleDateString(locale, { weekday: 'long' })}</small>
										{date.toLocaleDateString(locale, { day: 'numeric', month: 'long' })}
									</>
								) : (
									date.getDate()
								)}
							</h3>
							{presentation === 'planning' && !events.some((event) => localDay(new Date(event.start)) === day) && (
								<p className='biz-staffing-calendar__empty'>{t('STAFFING.NO_DAY_SHIFTS')}</p>
							)}
							{events
								.filter((event) => localDay(new Date(event.start)) === day)
								.map((event) => (
									<button
										key={event.id}
										type='button'
										className={
											event.isCancelled
												? 'is-cancelled'
												: event.kind
													? `is-${event.kind}`
													: event.warning
														? 'is-warning'
														: ''
										}
										title={event.isCancelled ? cancelledLabel : undefined}
										onClick={() => onOpen(event.id)}
									>
										<span className='biz-staffing-calendar__event-name'>
											{presentation !== 'planning' && (event.isCancelled || event.label) && (
												<>{event.isCancelled ? cancelledLabel : event.label} · </>
											)}
											<strong>{event.name}</strong>
										</span>
										<span className='biz-staffing-calendar__event-time'>
											<time dateTime={event.start}>{time(event.start)}</time>
											{event.end && (
												<>
													–<time dateTime={event.end}>{time(event.end)}</time>
												</>
											)}
											{presentation === 'planning' && (event.isCancelled || event.label) && (
												<> · {event.isCancelled ? cancelledLabel : event.label}</>
											)}
										</span>
										{presentation === 'planning' && (
											<>
												{event.projection && <span>{event.projection}</span>}
												<span className='biz-staffing-calendar__badges'>
													{event.badges?.map((b) => (
														<span
															key={b.text}
															className={
																b.tone ? `is-${b.tone}` : b.warning ? 'is-shortage' : 'is-staffed'
															}
														>
															{b.text}
														</span>
													))}
												</span>
											</>
										)}
									</button>
								))}
						</section>
					</Fragment>
				);
			})}
		</div>
	);
}
